/**
 * DeviceFormWizard.jsx
 * Formulário de Dispositivo em Etapas Premium
 *
 * 6 Passos:
 * 1. Básico - Nome, IMEI, Grupo (obrigatório)
 * 2. Veículo - Placa, Categoria, Marca, Modelo, Foto (opcional)
 * 3. Rastreador - Operadora, ICCID, Telefone (opcional)
 * 4. Documentos - Chassi, Renavam (opcional)
 * 5. Atributos - Atributos customizados (opcional)
 * 6. Revisão - Confirmar dados
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
 Box, Typography, Grid, MenuItem, IconButton, Tooltip, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Info as InfoIcon,
  DirectionsCar as CarIcon,
  Router as RouterIcon,
  Description as DocIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
  AddAPhoto as AddPhotoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { devicesActions } from '../../store';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import PremiumPlateInput from '../../common/components/premium/PremiumPlateInput';
import PremiumIMEIInput from '../../common/components/premium/PremiumIMEIInput';
import PremiumPhoneInput from '../../common/components/premium/PremiumPhoneInput';
import { PremiumSelect } from '../../common/components/premium/PremiumInput';
import SelectField from '../../common/components/SelectField';
import deviceCategories from '../../common/util/deviceCategories';
import EditAttributesAccordion from './EditAttributesAccordion';
import useDeviceAttributes from '../../common/attributes/useDeviceAttributes';
import useCommonDeviceAttributes from '../../common/attributes/useCommonDeviceAttributes';

// ============================================================================
// COLORS HELPER - Centraliza cores com tokens e fallbacks
// ============================================================================

const getWizardColors = (tokens) => ({
  // Accent colors
  accent: tokens?.accent?.primary || '#00d4aa',
  accentSubtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
  accentHover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
  accentMuted: tokens?.accent?.muted || 'rgba(0, 212, 170, 0.6)',
  // Text colors
  textPrimary: tokens?.text?.primary || '#f0f6fc',
  textSecondary: tokens?.text?.secondary || '#8b949e',
  textMuted: tokens?.text?.muted || '#6e7681',
  // Surface colors
  surfaceBase: tokens?.surface?.base || '#0d1117',
  surfaceElevated: tokens?.surface?.elevated || '#161b22',
  surfaceRaised: tokens?.surface?.raised || '#1c2128',
  surfaceHover: tokens?.surface?.hover || 'rgba(255, 255, 255, 0.05)',
  // Border colors
  borderSubtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
  borderDefault: tokens?.border?.default || 'rgba(255, 255, 255, 0.1)',
  // Status colors
  success: tokens?.status?.success || tokens?.accent?.primary || '#00d4aa',
  error: tokens?.status?.error || tokens?.semantic?.error || '#f85149',
});

// ============================================================================
// STEP 1: INFORMAÇÕES BÁSICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumIMEIInput
            label={`${t('deviceIdentifier')} *`}
            value={data.uniqueId || ''}
            onChange={(e) => updateData({ uniqueId: e.target.value })}
            error={!!errors?.uniqueId}
            skipLuhnValidation
            showIcon
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <SelectField
            fullWidth
            value={data.groupId || ''}
            onChange={(e) => updateData({ groupId: e.target.value ? Number(e.target.value) : null })}
            endpoint="/api/groups"
            label={t('groupParent')}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('deviceContact')}
            value={data.contact || ''}
            onChange={(e) => updateData({ contact: e.target.value })}
          />
        </Grid>
      </Grid>

      {/* Seção: Status e Configuração */}
      <Typography
        variant="overline"
        className="wizard-section-title"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('sharedExtra')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <SelectField
            fullWidth
            value={data.calendarId || ''}
            onChange={(e) => updateData({ calendarId: e.target.value ? Number(e.target.value) : null })}
            endpoint="/api/calendars"
            label={t('sharedCalendar')}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('userExpirationTime')}
            value={data.expirationTime ? data.expirationTime.split('T')[0] : ''}
            onChange={(e) => updateData({
              expirationTime: e.target.value ? `${e.target.value}T23:59:59.000+00:00` : null
            })}
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180, display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.disabled || false}
                onChange={(e) => updateData({ disabled: e.target.checked })}
                sx={{
                  color: colors.textMuted,
                  '&.Mui-checked': { color: colors.error },
                }}
              />
            }
            label={t('sharedDisabled')}
            sx={{
              '& .MuiFormControlLabel-label': {
                color: data.disabled ? colors.error : colors.textSecondary,
                fontSize: '14px',
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: DADOS DO VEÍCULO
// ============================================================================

const VehicleStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);
  const fileInputRef = useRef(null);

  // ID do dispositivo (disponível se estiver editando)
  const deviceId = data.id;

  // Estado local para preview de imagem nova (antes de salvar)
  const [imagePreview, setImagePreview] = useState(null);

  const handleFormField = (key, value) => {
    updateData({
      attributes: {
        ...data.attributes,
        form: {
          ...data.attributes?.form,
          [key]: value,
        },
      },
    });
  };

  const getFormValue = (key) => data.attributes?.form?.[key] || '';

  // Imagem existente (já salva no servidor)
  const existingImage = data.attributes?.deviceImage;

  // Handler para seleção de arquivo
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert(t('wizardImageInvalidType'));
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('wizardImageTooLarge'));
      return;
    }

    // Se dispositivo já existe, fazer upload imediato
    if (deviceId) {
      try {
        const response = await fetch(`/api/devices/${deviceId}/image`, {
          method: 'POST',
          body: file,
        });
        if (response.ok) {
          const imageName = await response.text();
          updateData({
            attributes: {
              ...data.attributes,
              deviceImage: imageName,
            },
          });
          setImagePreview(null);
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    } else {
      // Preview local + guardar arquivo para upload posterior
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      // Guardar arquivo no state para upload após criar device
      updateData({ _pendingImageFile: file });
    }
  };

  // Handler para remover imagem
  const handleRemoveImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Remover deviceImage dos attributes
    const newAttributes = { ...data.attributes };
    delete newAttributes.deviceImage;
    updateData({
      attributes: newAttributes,
      _pendingImageFile: null,
    });
  };

  // URL da imagem para preview
  const imageUrl = imagePreview || (existingImage ? `/api/media/${data.uniqueId}/${existingImage}` : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumPlateInput
            label={t('sharedDevicePlate')}
            value={getFormValue('plate')}
            onChange={(e) => handleFormField('plate', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <SelectField
            fullWidth
            value={data.category || 'default'}
            onChange={(e) => updateData({ category: e.target.value })}
            data={deviceCategories
              .map((category) => ({
                id: category,
                name: t(`category${category.replace(/^\w/, (c) => c.toUpperCase())}`),
              }))
              .filter((a) => a.name)
              .sort((a, b) => a.name.localeCompare(b.name))}
            label={t('deviceCategory')}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('deviceColor')}
            value={getFormValue('color')}
            onChange={(e) => handleFormField('color', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('deviceBrand')}
            value={getFormValue('brand')}
            onChange={(e) => handleFormField('brand', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('deviceModel')}
            value={getFormValue('model')}
            onChange={(e) => handleFormField('model', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('deviceFuelconsumption')}
            value={getFormValue('fuelConsumption')}
            onChange={(e) => handleFormField('fuelConsumption', e.target.value)}
            type="number"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('deviceYear')}
            value={getFormValue('year')}
            onChange={(e) => handleFormField('year', e.target.value)}
            type="number"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('deviceOdometer')}
            value={getFormValue('initialOdometer')}
            onChange={(e) => handleFormField('initialOdometer', e.target.value)}
            type="number"
          />
        </Grid>
      </Grid>

      {/* Seção: Foto do Veículo */}
      <Typography
        variant="overline"
        className="wizard-section-title"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('attributeDeviceImage')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Preview da Imagem */}
        <Box
          sx={{
            width: 180,
            height: 120,
            borderRadius: '12px',
            border: `2px dashed ${imageUrl ? colors.accent : colors.borderDefault}`,
            backgroundColor: colors.surfaceHover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: colors.accent,
              backgroundColor: colors.accentSubtle,
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          {imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt={t('attributeDeviceImage')}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {/* Overlay com ações */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: tokens?.overlay?.medium || 'rgba(0,0,0,0.5)',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  '&:hover': { opacity: 1 },
                }}
              >
                <Tooltip title={t('sharedChange')}>
                  <IconButton
                    size="small"
                    sx={{
                      color: tokens?.text?.inverse || '#ffffff',
                      backgroundColor: tokens?.overlay?.light || 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <AddPhotoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('sharedRemove')}>
                  <IconButton
                    size="small"
                    sx={{
                      color: tokens?.text?.inverse || '#ffffff',
                      backgroundColor: tokens?.overlay?.light || 'rgba(255,255,255,0.2)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <AddPhotoIcon sx={{ fontSize: 32, color: colors.textMuted, mb: 1 }} />
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
                {t('wizardClickToAddPhoto')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Info text */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
            {t('wizardImageHelp')}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {t('wizardImageFormats')}
          </Typography>
          {!deviceId && imagePreview && (
            <Typography variant="caption" sx={{ color: colors.accent, display: 'block', mt: 1 }}>
              ✓ {t('wizardImagePending')}
            </Typography>
          )}
        </Box>

        {/* Input file escondido */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 3: RASTREADOR E CHIP
// ============================================================================

const TrackerStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const handleFormField = (key, value) => {
    updateData({
      attributes: {
        ...data.attributes,
        form: {
          ...data.attributes?.form,
          [key]: value,
        },
      },
    });
  };

  const getFormValue = (key) => data.attributes?.form?.[key] || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('sharedEquipmentModel')}
            value={data.model || ''}
            onChange={(e) => updateData({ model: e.target.value })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumPhoneInput
            label={t('sharedPhone')}
            value={data.phone || ''}
            onChange={(e) => updateData({ phone: e.target.value })}
          />
        </Grid>
      </Grid>

      <Typography
        variant="overline"
        className="wizard-section-title"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('wizardChipInfo')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedOperatorChip')}
            value={getFormValue('operator')}
            onChange={(e) => handleFormField('operator', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedVendorChip')}
            value={getFormValue('vendor')}
            onChange={(e) => handleFormField('vendor', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedIMEIChip')}
            value={getFormValue('iccid')}
            onChange={(e) => handleFormField('iccid', e.target.value)}
            inputProps={{ maxLength: 22, style: { fontFamily: 'monospace' } }}
          />
        </Grid>
      </Grid>

      <Typography
        variant="overline"
        className="wizard-section-title"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('wizardInstallation')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('sharedInstallationDate')}
            value={getFormValue('installationDate')}
            onChange={(e) => handleFormField('installationDate', e.target.value)}
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('deviceInstaller')}
            value={getFormValue('installer')}
            onChange={(e) => handleFormField('installer', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 4: DOCUMENTAÇÃO
// ============================================================================

const DocumentsStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const handleFormField = (key, value) => {
    updateData({
      attributes: {
        ...data.attributes,
        form: {
          ...data.attributes?.form,
          [key]: value,
        },
      },
    });
  };

  const getFormValue = (key) => data.attributes?.form?.[key] || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography
        variant="overline"
        className="wizard-section-title"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('wizardVehicleId')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedPrefixo')}
            value={getFormValue('prefix')}
            onChange={(e) => handleFormField('prefix', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedChassi')}
            value={getFormValue('chassi')}
            onChange={(e) => handleFormField('chassi', e.target.value.toUpperCase())}
            inputProps={{ maxLength: 17, style: { textTransform: 'uppercase' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedRenavam')}
            value={getFormValue('vin')}
            onChange={(e) => handleFormField('vin', e.target.value)}
            inputProps={{ maxLength: 11 }}
          />
        </Grid>
      </Grid>

      <Typography
        variant="overline"
        className="wizard-section-title"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('wizardOwner')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('deviceOwnerName')}
            value={getFormValue('ownerName')}
            onChange={(e) => handleFormField('ownerName', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('deviceOwnerDocument')}
            value={getFormValue('ownerDocument')}
            onChange={(e) => handleFormField('ownerDocument', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={12}>
          <PremiumInput
            label={t('sharedDescription')}
            value={getFormValue('notes')}
            onChange={(e) => handleFormField('notes', e.target.value)}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 5: ATRIBUTOS CUSTOMIZADOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  // Hooks de atributos
  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const deviceAttributes = useDeviceAttributes(t);

  // Filtrar atributos (remover 'form' que é usado internamente)
  const filterAttributes = (attributes) => {
    if (!attributes) return {};
    const filtered = {};
    Object.keys(attributes).forEach((key) => {
      if (key !== 'form' && key !== 'deviceImage') {
        filtered[key] = attributes[key];
      }
    });
    return filtered;
  };

  // Atualizar atributos preservando 'form' e 'deviceImage'
  const handleAttributesChange = (newAttributes) => {
    updateData({
      attributes: {
        ...newAttributes,
        form: data.attributes?.form || {},
        ...(data.attributes?.deviceImage && { deviceImage: data.attributes.deviceImage }),
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Info banner */}
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp')}
        </Typography>
      </Box>

      {/* Attributes Accordion */}
      <EditAttributesAccordion
        attributes={filterAttributes(data.attributes)}
        setAttributes={handleAttributesChange}
        definitions={{ ...commonDeviceAttributes, ...deviceAttributes }}
      />
    </Box>
  );
};

// ============================================================================
// STEP 6: REVISÃO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  // Imagem do veículo (existente ou pendente)
  const existingImage = data.attributes?.deviceImage;
  const hasPendingImage = !!data._pendingImageFile;

  const InfoRow = ({ label, value }) => (
    <Box
      className="wizard-review-row"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: `1px solid ${colors.borderSubtle}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" sx={{ color: colors.textMuted, fontSize: '13px' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: colors.textPrimary,
          fontWeight: 500,
          textAlign: 'right',
          maxWidth: '60%',
          fontSize: '13px',
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  );

  const Section = ({ title, children }) => (
    <Box
      className="wizard-review-section"
      sx={{
        backgroundColor: colors.surfaceHover,
        borderRadius: '8px',
        p: 2,
        border: `1px solid ${colors.borderSubtle}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: colors.accent,
          mb: 2,
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  const getFormValue = (key) => data.attributes?.form?.[key] || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Success banner */}
      <Box
        sx={{
          p: 2.5,
          borderRadius: '10px',
          backgroundColor: colors.accentSubtle,
          border: `1px solid ${colors.accentHover}`,
        }}
      >
        <Typography sx={{ color: colors.accent, fontWeight: 500, fontSize: '14px' }}>
          ✓ {t('wizardReviewBanner')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Identificação */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('wizardIdentification')}>
            <InfoRow label={t('sharedName')} value={data.name} />
            <InfoRow label={t('deviceIdentifier')} value={data.uniqueId} />
            <InfoRow label={t('deviceContact')} value={data.contact} />
            <InfoRow
              label={t('userExpirationTime')}
              value={data.expirationTime ? new Date(data.expirationTime).toLocaleDateString() : null}
            />
            <InfoRow
              label={t('sharedDisabled')}
              value={data.disabled ? t('sharedYes') : null}
            />
          </Section>
        </Grid>

        {/* Veículo */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('wizardVehicleSection')}>
            <InfoRow label={t('sharedDevicePlate')} value={getFormValue('plate')} />
            <InfoRow label={t('deviceCategory')} value={data.category !== 'default' ? data.category : '-'} />
            <InfoRow
              label={t('wizardBrandModel')}
              value={[getFormValue('brand'), getFormValue('model')].filter(Boolean).join(' ') || '-'}
            />
            <InfoRow label={t('deviceColor')} value={getFormValue('color')} />
            <InfoRow label={t('deviceYear')} value={getFormValue('year')} />
            {/* Imagem do veículo */}
            {(existingImage || hasPendingImage) && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderSubtle}` }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
                  {t('attributeDeviceImage')}
                </Typography>
                <Box
                  sx={{
                    width: 80,
                    height: 60,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: `1px solid ${colors.borderSubtle}`,
                  }}
                >
                  {existingImage ? (
                    <img
                      src={`/api/media/${data.uniqueId}/${existingImage}`}
                      alt={t('attributeDeviceImage')}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : hasPendingImage && (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: colors.accentSubtle,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: colors.accent, fontSize: 10 }}>
                        {t('wizardImagePendingShort')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Section>
        </Grid>

        {/* Rastreador */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('wizardTrackerSection')}>
            <InfoRow label={t('sharedEquipmentModel')} value={data.model} />
            <InfoRow label={t('sharedPhone')} value={data.phone} />
            <InfoRow label={t('sharedOperatorChip')} value={getFormValue('operator')} />
            <InfoRow label="ICCID" value={getFormValue('iccid')} />
          </Section>
        </Grid>

        {/* Documentação */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('wizardDocumentation')}>
            <InfoRow label={t('sharedPrefixo')} value={getFormValue('prefix')} />
            <InfoRow label={t('sharedChassi')} value={getFormValue('chassi')} />
            <InfoRow label={t('sharedRenavam')} value={getFormValue('vin')} />
            <InfoRow label={t('sharedOwnerName')} value={getFormValue('ownerName')} />
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DeviceFormWizard = ({ device, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('wizardBasicTitle'),
      description: t('wizardBasicDescription'),
      icon: <InfoIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('wizardErrorRequired');
        if (!data.uniqueId?.trim()) errors.uniqueId = t('wizardErrorRequired');
        if (data.uniqueId && data.uniqueId.length < 5) {
          errors.uniqueId = t('wizardErrorIdShort');
        }
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
        { label: t('deviceIdentifier'), value: data.uniqueId },
      ],
    },
    {
      label: t('wizardStepVehicle'),
      title: t('wizardVehicleTitle'),
      description: t('wizardVehicleDescription'),
      icon: <CarIcon sx={{ fontSize: 18 }} />,
      component: VehicleStep,
      optional: true,
      getSummary: (data) => [
        { label: t('sharedDevicePlate'), value: data.attributes?.form?.plate },
        { label: t('deviceCategory'), value: data.category !== 'default' ? data.category : null },
      ],
    },
    {
      label: t('wizardStepTracker'),
      title: t('wizardTrackerTitle'),
      description: t('wizardTrackerDescription'),
      icon: <RouterIcon sx={{ fontSize: 18 }} />,
      component: TrackerStep,
      optional: true,
      getSummary: (data) => [
        { label: t('deviceModel'), value: data.model },
        { label: t('sharedPhone'), value: data.phone },
      ],
    },
    {
      label: t('wizardStepDocs'),
      title: t('wizardDocsTitle'),
      description: t('wizardDocsDescription'),
      icon: <DocIcon sx={{ fontSize: 18 }} />,
      component: DocumentsStep,
      optional: true,
    },
    {
      label: t('wizardStepAttributes'),
      title: t('wizardAttributesTitle'),
      description: t('wizardAttributesDescription'),
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      // Extrair arquivo pendente antes de enviar (não deve ir no JSON)
      const pendingImageFile = data._pendingImageFile;
      const cleanData = { ...data };
      delete cleanData._pendingImageFile;

      const url = device?.id ? `/api/devices/${device.id}` : '/api/devices';
      const method = device?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData),
        credentials: 'same-origin',
      });

      if (response.ok) {
        const savedDevice = await response.json();

        // Se há imagem pendente e o dispositivo foi criado/atualizado, fazer upload
        if (pendingImageFile && savedDevice.id) {
          try {
            const imageResponse = await fetch(`/api/devices/${savedDevice.id}/image`, {
              method: 'POST',
              body: pendingImageFile,
            });
            if (imageResponse.ok) {
              const imageName = await imageResponse.text();
              // Atualizar o dispositivo com a imagem
              savedDevice.attributes = {
                ...savedDevice.attributes,
                deviceImage: imageName,
              };
            }
          } catch (imageError) {
            console.error('Image upload error:', imageError);
            // Continuar mesmo se o upload falhar - dispositivo já foi salvo
          }
        }

        dispatch(devicesActions.refresh([savedDevice]));
        onSave?.(savedDevice);
        navigate('/settings/devices');
      } else {
        // Log detalhado do erro
        const errorText = await response.text();
        console.error('[DeviceFormWizard] API Error:', response.status, errorText);

        // Traduzir erros comuns
        let errorMessage = t('wizardErrorSave');
        if (errorText.includes('Duplicate entry') && errorText.includes('uniqueid')) {
          errorMessage = t('wizardErrorDuplicateId') || 'Este identificador (IMEI) já está cadastrado. Use um identificador único.';
        } else if (errorText.includes('Duplicate entry')) {
          errorMessage = t('wizardErrorDuplicate') || 'Registro duplicado. Verifique os dados.';
        } else {
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorMessage;
          } catch {
            // Se não for JSON, usar texto truncado
            errorMessage = errorText.length > 100 ? `${errorText.substring(0, 100)}...` : errorText || errorMessage;
          }
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Save error:', error);
      // Mostrar erro para o usuário
      alert(error.message || t('wizardErrorSave'));
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/devices');
  };

  const initialData = device || {
    name: '',
    uniqueId: '',
    category: 'default',
    attributes: { form: {} },
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={device?.id ? null : 'device-wizard-draft'}
      completeButtonText={device?.id ? t('wizardSaveChanges') : t('wizardRegisterDevice')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('wizardBack')}
      cancelButtonText={t('wizardCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default DeviceFormWizard;
